(() => {
  if (window.__midgardMarketPlugCartLoaded) {
    return;
  }

  window.__midgardMarketPlugCartLoaded = true;

  const configElement = document.querySelector('[data-market-plug-cart-config]');

  if (!configElement) {
    return;
  }

  let config;

  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    console.error('Market plug cart: invalid configuration.', error);
    return;
  }

  const canonicalPlug = config.canonicalPlug;
  const initialItemCount = Number(config.initialItemCount || 0);
  const plugAliases = {
    EU: ['EU'],
    UK: ['UK'],
    US: ['US'],
    AU: ['AU', 'AUS'],
    JP: ['JP', 'JPN'],
    KR: ['KR', 'KOR'],
    TW: ['TW', 'TWN']
  };
  const knownPlugValues = new Set(
    Object.values(plugAliases).flat()
  );
  const productCache = new Map();
  let correctionRunning = false;
  let cartChangeObserved = false;

  const cartRoot =
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  const normalize = value => String(value || '').trim().toUpperCase();

  const isPlugOptionName = name => {
    const normalizedName = normalize(name);
    return normalizedName === 'PLUG' || normalizedName === 'NETZSTECKER';
  };

  const normalizeProductOptions = product =>
    (Array.isArray(product.options) ? product.options : []).map((option, index) => {
      const valuesFromVariants = (product.variants || [])
        .map(variant => variant.options && variant.options[index])
        .filter(Boolean);

      return {
        name: typeof option === 'string' ? option : option.name,
        values: typeof option === 'string'
          ? [...new Set(valuesFromVariants)]
          : option.values || [...new Set(valuesFromVariants)]
      };
    });

  const isPlugOption = option => {
    if (isPlugOptionName(option.name)) {
      return true;
    }

    const knownValueCount = (option.values || [])
      .map(value => normalize(value && value.name ? value.name : value))
      .filter(value => knownPlugValues.has(value)).length;

    return knownValueCount >= 3;
  };

  const getCart = async () => {
    const response = await fetch(cartRoot + 'cart.js', {
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error('Cart could not be read: ' + response.status);
    }

    return response.json();
  };

  const getHandle = item => {
    if (item.handle) {
      return item.handle;
    }

    try {
      const url = new URL(item.url, window.location.origin);
      const parts = url.pathname.split('/').filter(Boolean);
      const productIndex = parts.lastIndexOf('products');
      return productIndex >= 0 ? parts[productIndex + 1] : null;
    } catch (error) {
      return null;
    }
  };

  const getProduct = handle => {
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(cartRoot + 'products/' + encodeURIComponent(handle) + '.js', {
          headers: { Accept: 'application/json' }
        }).then(response => {
          if (!response.ok) {
            throw new Error(
              'Product could not be read: ' + handle + ' (' + response.status + ')'
            );
          }

          return response.json();
        })
      );
    }

    return productCache.get(handle);
  };

  const itemCouldContainPlug = item => {
    const namedOptions = Array.isArray(item.options_with_values)
      ? item.options_with_values
      : [];

    if (namedOptions.some(option => isPlugOptionName(option.name))) {
      return true;
    }

    return (item.variant_options || [])
      .map(normalize)
      .some(value => knownPlugValues.has(value));
  };

  const resolveSupportedItems = async cart => {
    const candidates = cart.items.filter(itemCouldContainPlug);
    const handles = [...new Set(candidates.map(getHandle).filter(Boolean))];
    const productsByHandle = new Map();

    await Promise.all(
      handles.map(async handle => {
        productsByHandle.set(handle, await getProduct(handle));
      })
    );

    return candidates.map(item => {
      const handle = getHandle(item);
      const product = productsByHandle.get(handle);

      if (!product) {
        return null;
      }

      const productOptions = normalizeProductOptions(product);
      const plugOptionIndex = productOptions.findIndex(isPlugOption);

      if (plugOptionIndex < 0) {
        return null;
      }

      const availablePlugValues = productOptions[plugOptionIndex].values
        .map(value => normalize(value && value.name ? value.name : value));
      const expectedPlug = (plugAliases[canonicalPlug] || [])
        .find(alias => availablePlugValues.includes(alias));

      if (!expectedPlug) {
        return null;
      }

      return {
        item,
        product,
        plugOptionIndex,
        expectedPlug
      };
    }).filter(Boolean);
  };

  const hasLineProperties = item =>
    item.properties && Object.keys(item.properties).length > 0;

  const getCurrentPlug = entry =>
    normalize((entry.item.variant_options || [])[entry.plugOptionIndex]);

  const correctCart = async suppliedCart => {
    if (correctionRunning) {
      return;
    }

    correctionRunning = true;

    try {
      const cart = suppliedCart || await getCart();
      const supportedItems = await resolveSupportedItems(cart);
      const wrongItems = supportedItems.filter(
        entry => getCurrentPlug(entry) !== entry.expectedPlug
      );

      if (wrongItems.length === 0) {
        return;
      }

      const unsafeEntry = supportedItems.find(
        entry => hasLineProperties(entry.item) || entry.item.selling_plan_allocation
      );

      if (unsafeEntry) {
        console.error(
          'Market plug cart stopped: line properties or a selling plan are present.',
          unsafeEntry.item
        );
        return;
      }

      const updates = {};
      const targetQuantities = {};

      for (const entry of supportedItems) {
        const selectedOptions = entry.item.variant_options || [];

        if (selectedOptions.length <= entry.plugOptionIndex) {
          console.error('Market plug cart stopped: invalid supported item.', entry.item);
          return;
        }

        const targetVariant = (entry.product.variants || []).find(variant =>
          variant.available === true &&
          variant.options.length === selectedOptions.length &&
          variant.options.every((value, index) =>
            index === entry.plugOptionIndex
              ? normalize(value) === entry.expectedPlug
              : value === selectedOptions[index]
          )
        );

        if (!targetVariant) {
          console.error(
            'Market plug cart stopped: no available target variant.',
            entry.item
          );
          return;
        }

        const targetId = String(targetVariant.id);
        targetQuantities[targetId] =
          (targetQuantities[targetId] || 0) + entry.item.quantity;
      }

      Object.assign(updates, targetQuantities);

      for (const entry of wrongItems) {
        updates[entry.item.key] = 0;
      }

      const response = await fetch(cartRoot + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        throw new Error('Cart could not be corrected: ' + response.status);
      }

      const verifiedCart = await getCart();
      const verifiedItems = await resolveSupportedItems(verifiedCart);
      const remainingWrongItem = verifiedItems.find(
        entry => getCurrentPlug(entry) !== entry.expectedPlug
      );

      if (remainingWrongItem) {
        console.error(
          'Market plug cart verification failed.',
          remainingWrongItem.item
        );
        return;
      }

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true })
      );
    } catch (error) {
      console.error('Market plug cart:', error);
    } finally {
      correctionRunning = false;
    }
  };

  document.addEventListener('cart:change', event => {
    cartChangeObserved = true;
    correctCart(event.detail && event.detail.cart);
  });

  const runInitialCorrection = () => {
    if (!cartChangeObserved && initialItemCount > 0) {
      correctCart();
    }
  };

  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      correctCart();
    }
  });

  if (window.themeVariables?.settings?.pageType === 'cart') {
    runInitialCorrection();
  } else if ('requestIdleCallback' in window) {
    window.requestIdleCallback(runInitialCorrection, { timeout: 2000 });
  } else {
    window.setTimeout(runInitialCorrection, 1000);
  }
})();